import { useLayoutEffect, useState } from 'react';

/*
 * The canvas fit of the analysis workbench (docs/ANALYSIS_WORKBENCH.md
 * section 6): a picture contained in the stage, as large as it fits and
 * centred, whatever its aspect.
 *
 * The stage's size is the only input, and nothing a person does inside the
 * workbench changes it: drawers, notices, the batch bar and popovers are all
 * overlays (absolute children of the stage), and the top bar and the toolbar
 * have fixed heights. Only the window changes it (a resize, a rotation).
 */

/**
 * The box of a `image` (width x height, any unit) contained in `stage`
 * (width x height px), centred, `pad` px clear of every edge: {left, top,
 * width, height, scale}, scale being stage px per image unit. Null while the
 * stage has no size yet. A picture whose size is not known yet is a square.
 */
export function fitBox(stage, image, pad = 0) {
  const sw = Math.max(0, (Number(stage?.width) || 0) - 2 * pad);
  const sh = Math.max(0, (Number(stage?.height) || 0) - 2 * pad);
  if (!sw || !sh) return null;
  const iw = Number(image?.width) > 0 ? Number(image.width) : 1;
  const ih = Number(image?.height) > 0 ? Number(image.height) : 1;
  const scale = Math.min(sw / iw, sh / ih);
  const width = iw * scale;
  const height = ih * scale;
  return { left: pad + (sw - width) / 2, top: pad + (sh - height) / 2, width, height, scale };
}

const same = (a, b) => !!a && !!b && a.width === b.width && a.height === b.height;

/**
 * The size of the element `ref` points at, {width, height} px, kept up to
 * date by a ResizeObserver (the window's resize where the browser has none);
 * null until it is first measured.
 */
export function useStageSize(ref) {
  const [size, setSize] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
      setSize(prev => (same(prev, next) ? prev : next));
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref]);
  return size;
}
